import { useEffect, useState } from "react";
import { Zap } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { api, money, formatApiError, startCheckout } from "../lib/api";
import { useTripTypes, earliestDate, latestDate, windowText } from "../lib/tripTypes";

const Row = ({ label, value, tid, strong }) => (
  <div className={`flex justify-between text-sm ${strong ? "pt-3 border-t border-white/10 font-semibold" : "text-slate-300"}`}>
    <span>{label}</span><span data-testid={tid} className="font-mono-lux">{value}</span>
  </div>
);

export const BuyNowCard = ({ m, ts }) => {
  const [open, setOpen] = useState(false);
  const [quote, setQuote] = useState(null);
  const [vacs, setVacs] = useState([]);
  const [pick, setPick] = useState("");
  const [ack, setAck] = useState(false);
  const [busy, setBusy] = useState(false);
  const [date, setDate] = useState("");
  const types = useTripTypes();
  const tt = types[vacs.find((v) => v.id === pick)?.trip_type || "weekend"];
  const minD = earliestDate(tt?.min || 1);

  useEffect(() => {
    if (!open) return;
    api.get(`/memberships/${m.tier}/buy-now-quote`).then((r) => setQuote(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get(`/vacations?tier=${m.tier}`).then((r) => {
      const ok = r.data.filter((v) => m.level === "premium" || !v.premium_only);
      setVacs(ok);
      setPick(ok[0]?.id || "");
    });
  }, [open, m.tier, m.level]);

  const go = async () => {
    setBusy(true);
    try { await startCheckout({ kind: "buy_now", tier: m.tier, vacation_id: pick, travel_date: date }); }
    catch (e) { toast.error(formatApiError(e)); setBusy(false); }
  };

  return (
    <div data-testid="buy-now-card" className="lux-card p-6">
      <p className="eyebrow"><Zap className="w-3.5 h-3.5 mr-1.5" />Buy it now</p>
      <p className="text-sm text-slate-300 mt-4">Don't want to wait? Pay the rest of your trip yourself and go now. Your payments in this line so far count toward it.</p>
      <button data-testid="buy-now-open-btn" onClick={() => setOpen(true)} className="btn-ghost px-5 py-2.5 text-sm mt-4">See my buy-it-now price</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="buy-now-dialog" className="bg-[#13192B] border-white/10 text-white max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif-lux text-2xl">Buy your trip now · {ts.label}</DialogTitle></DialogHeader>
          <label className="block"><span className="text-xs text-slate-400">Choose your trip</span>
            <select data-testid="buy-now-vacation-select" value={pick} onChange={(e) => setPick(e.target.value)} className="lux-input mt-1">
              {vacs.map((v) => <option key={v.id} value={v.id}>{`${v.title} — ${v.location}`}</option>)}
            </select></label>
        <label className="block ">
          <span className="text-xs text-slate-400">Travel date · {tt?.label} ({windowText(tt)}, so partners can book in time)</span>
          <input data-testid="buy-now-travel-date" type="date" min={minD} max={latestDate()} value={date} onChange={(e) => setDate(e.target.value)} className="lux-input mt-2" />
        </label>
          {quote && (
            <div className="space-y-2 rounded-2xl bg-white/5 p-4">
              <Row label="Your trip budget" value={money(quote.trip_cost)} tid="buy-now-trip-cost" />
              <Row label="Your payments in this line (credit)" value={`− ${money(quote.pool_credit)}`} tid="buy-now-credit" />
              <Row label="Your extra trip fund" value={`− ${money(quote.extra_balance)}`} tid="buy-now-extra" />
              <Row label="Remaining trip cost" value={money(quote.subtotal)} tid="buy-now-subtotal" />
              <Row label={`Buy-it-now fee (${quote.fee_pct}%)`} value={`+ ${money(quote.fee)}`} tid="buy-now-fee" />
              <Row label="You pay now" value={money(quote.remaining)} tid="buy-now-remaining" strong />
            </div>
          )}
          <label className="flex items-start gap-3 text-sm text-slate-300 cursor-pointer">
            <input data-testid="buy-now-ack-checkbox" type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="accent-[#38BDF8] w-4 h-4 mt-0.5" />
            I understand this one-time payment is non-refundable, my trip is booked right away, and I leave this line (autopay for it stops).
          </label>
          <button data-testid="buy-now-confirm-btn" disabled={!quote || !pick || !ack || busy || !date || date < minD} onClick={go} className="btn-gold w-full py-3.5">
            {busy ? "Redirecting to Stripe…" : `Buy it now · ${quote ? money(quote.remaining) : "…"}`}
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
};
